import React, { useState, useEffect } from 'react';
import { Table, Badge, Form, Modal, Button, Row, Col } from 'react-bootstrap';
import { usersAPI, leaveAPI, wfhAPI } from '../services/api';
import Pagination from '../components/Pagination';
import { FaSearch, FaEdit, FaUmbrellaBeach, FaHome } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useAuth } from '../contexts/AuthContext';
import { formatDateIST } from '../utils/dateFormatter';
import { DEPARTMENTS } from '../constants/departments';

const Users = () => {
  const { isAdmin, isHR } = useAuth();
  const [users, setUsers] = useState([]);
  const [allUsersForManagers, setAllUsersForManagers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [employeesOnLeave, setEmployeesOnLeave] = useState([]);
  const [employeesOnWFH, setEmployeesOnWFH] = useState([]);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    phone: '',
    employee_code: '',
    designation: '',
    department: '',
    manager_id: '',
    is_active: true
  });
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Add Employee states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    employee_code: '',
    designation: '',
    department: '',
    role: 'employee',
    manager_id: '',
    date_of_joining: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchAllUsersForManagers();
    fetchEmployeesOnLeave();
    fetchEmployeesOnWFH();
  }, []);

  const fetchUsers = async () => {
    try {
      // Fetch with excluded emails for the main user list
      const response = await usersAPI.getAllUsersWithAttendance(true);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAllUsersForManagers = async () => {
    try {
      // Fetch without excluding emails for reporting manager dropdown
      const response = await usersAPI.getAllUsersWithAttendance(false);
      setAllUsersForManagers(response.data);
    } catch (error) {
      console.error('Error fetching users for managers:', error);
    }
  };

  const fetchEmployeesOnLeave = async () => {
    try {
      const response = await leaveAPI.getTodayLeaves();
      setEmployeesOnLeave(response.data);
    } catch (error) {
      console.error('Error fetching employees on leave:', error);
    }
  };

  const fetchEmployeesOnWFH = async () => {
    try {
      const response = await wfhAPI.getTodayWFH();
      setEmployeesOnWFH(response.data);
    } catch (error) {
      console.error('Error fetching employees on WFH:', error);
    }
  };

  const getRoleBadge = (role) => {
    const colors = { admin: 'danger', hr: 'warning', employee: 'primary' };
    return <Badge bg={colors[role]}>{role}</Badge>;
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      full_name: user.full_name || '',
      phone: user.phone || '',
      employee_code: user.employee_code || '',
      designation: user.designation || '',
      department: user.department || '',
      manager_id: user.manager_id || '',
      is_active: user.is_active !== undefined ? user.is_active : true
    });
    setResetPassword('');
    setShowResetPassword(false);
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    try {
      // Filter out empty strings and null values, only send fields with actual values
      const updateData = {};
      Object.keys(editFormData).forEach(key => {
        const value = editFormData[key];
        // Include the field if it has a value (not empty string, not null, not undefined)
        // Also handle manager_id: null explicitly (when user selects "No Manager")
        if (key === 'manager_id') {
          // Only include manager_id if it's explicitly set (number or null from dropdown)
          if (value !== '' && value !== undefined) {
            updateData[key] = value;
          }
        } else if (key === 'is_active') {
          // Always include is_active
          updateData[key] = value;
        } else if (value !== '' && value !== null && value !== undefined) {
          updateData[key] = value;
        }
      });

      // Add password reset if provided
      if (resetPassword && resetPassword.trim() !== '') {
        updateData.new_password = resetPassword;
      }

      await usersAPI.updateUser(selectedUser.id, updateData);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Employee details updated successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
      setShowEditModal(false);
      setResetPassword('');
      setShowResetPassword(false);
      fetchUsers();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to update employee details',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const handleAddEmployee = async () => {
    try {
      // Validate required fields
      if (!addFormData.full_name || !addFormData.email || !addFormData.phone) {
        Swal.fire({
          icon: 'warning',
          title: 'Missing Information',
          text: 'Please fill in Full Name, Email, and Mobile Number',
          confirmButtonColor: '#635bff'
        });
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(addFormData.email)) {
        Swal.fire({
          icon: 'warning',
          title: 'Invalid Email',
          text: 'Please enter a valid email address',
          confirmButtonColor: '#635bff'
        });
        return;
      }

      // Validate phone number
      if (addFormData.phone.length !== 10 || !/^\d+$/.test(addFormData.phone)) {
        Swal.fire({
          icon: 'warning',
          title: 'Invalid Phone Number',
          text: 'Please enter a valid 10-digit mobile number',
          confirmButtonColor: '#635bff'
        });
        return;
      }

      // Prepare data for submission
      const submitData = {};
      Object.keys(addFormData).forEach(key => {
        const value = addFormData[key];
        if (key === 'manager_id') {
          if (value !== '' && value !== undefined) {
            submitData[key] = parseInt(value);
          }
        } else if (value !== '' && value !== null && value !== undefined) {
          submitData[key] = value;
        }
      });

      await usersAPI.createUser(submitData);

      Swal.fire({
        icon: 'success',
        title: 'Employee Added!',
        html: `
          <p>Employee has been successfully added to the system.</p>
          <p class="text-muted" style="font-size: 14px; margin-top: 12px;">
            Welcome instructions have been sent to <strong>${addFormData.email}</strong>
          </p>
        `,
        confirmButtonColor: '#635bff',
        timer: 3000,
        showConfirmButton: true
      });

      setShowAddModal(false);
      setAddFormData({
        full_name: '',
        email: '',
        phone: '',
        employee_code: '',
        designation: '',
        department: '',
        role: 'employee',
        manager_id: '',
        date_of_joining: ''
      });
      fetchUsers();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to add employee',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const getManagerName = (managerId) => {
    if (!managerId) return 'No Manager';
    // Search in allUsersForManagers to include excluded emails (like founders/admins)
    const manager = allUsersForManagers.find(u => u.id === managerId);
    return manager ? manager.full_name : 'Unknown';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : `User #${userId}`;
  };

  const formatTime = (datetime) => {
    if (!datetime) return '-';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatWorkingHours = (hours) => {
    if (!hours && hours !== 0) return '-';
    return `${hours} hrs`;
  };

  const TOTAL_WORKING_HOURS = 8;

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      {/* Header */}
      <div style={{
        marginBottom: '32px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#0a0a0a',
          letterSpacing: '-0.02em',
          margin: 0
        }}>
          Employee Management
        </h1>
      </div>

      {/* Status Cards */}
      <Row className="mb-4">
        <Col md={6}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
            padding: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaUmbrellaBeach size={20} style={{ color: '#92400e' }} />
              </div>
              <div>
                <h5 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#0a0a0a'
                }}>
                  On Leave Today
                </h5>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  {employeesOnLeave.length} {employeesOnLeave.length === 1 ? 'employee' : 'employees'}
                </p>
              </div>
            </div>
            {employeesOnLeave.length > 0 ? (
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {employeesOnLeave.map((leave) => (
                  <div key={leave.id} style={{
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                    marginBottom: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#0a0a0a'
                      }}>
                        {getUserName(leave.user_id)}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280'
                      }}>
                        {formatDateIST(leave.start_date)} - {formatDateIST(leave.end_date)}
                      </div>
                    </div>
                    <Badge bg="warning" style={{ textTransform: 'capitalize' }}>
                      {leave.leave_type}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: '#9ca3af',
                fontSize: '14px'
              }}>
                No employees on leave today
              </div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
            padding: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaHome size={20} style={{ color: '#166534' }} />
              </div>
              <div>
                <h5 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#0a0a0a'
                }}>
                  Working From Home Today
                </h5>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  {employeesOnWFH.length} {employeesOnWFH.length === 1 ? 'employee' : 'employees'}
                </p>
              </div>
            </div>
            {employeesOnWFH.length > 0 ? (
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {employeesOnWFH.map((wfh) => (
                  <div key={wfh.id} style={{
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                    marginBottom: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#0a0a0a'
                      }}>
                        {getUserName(wfh.user_id)}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280'
                      }}>
                        {formatDateIST(wfh.start_date)} - {formatDateIST(wfh.end_date)}
                      </div>
                    </div>
                    <Badge bg="success">WFH</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: '#9ca3af',
                fontSize: '14px'
              }}>
                No employees working from home today
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Users Table Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#faf9ff'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <h5 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#0a0a0a'
              }}>
                Employees Directory
              </h5>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                Total: {filteredUsers.length} {filteredUsers.length === 1 ? 'employee' : 'employees'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {(isAdmin || isHR) && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    backgroundColor: '#635bff',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                >
                  + Add Employee
                </Button>
              )}
              <div style={{ position: 'relative', minWidth: '300px' }}>
              <FaSearch style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                fontSize: '14px'
              }} />
              <Form.Control
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  paddingLeft: '40px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px'
                }}
              />
              </div>
            </div>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table hover responsive style={{ margin: 0 }}>
            <thead style={{ backgroundColor: '#faf9ff' }}>
              <tr>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '5%'
                }}>#</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '35%'
                }}>Employee</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '25%'
                }}>Role & Department</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '20%'
                }}>Reporting To</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '10%'
                }}>Status</th>
                {(isAdmin || isHR) && (
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb',
                    width: '15%'
                  }}>Today's Attendance</th>
                )}
                {(isAdmin || isHR) && (
                  <th style={{
                    padding: '16px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #e5e7eb',
                    width: '5%'
                  }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={(isAdmin || isHR) ? "7" : "5"} style={{
                    padding: '60px 20px',
                    textAlign: 'center',
                    color: '#9ca3af',
                    fontSize: '14px'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                      {searchTerm ? 'No matching users found' : 'No users found'}
                    </div>
                    <div>{searchTerm ? 'Try a different search term' : 'User records will appear here'}</div>
                  </td>
                </tr>
              ) : (
                currentUsers.map((user, index) => {
                  return (
                    <tr
                      key={user.id}
                      style={{
                        borderBottom: '1px solid #e5e7eb'
                      }}
                    >
                      {/* Serial Number */}
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#6b7280'
                      }}>
                        {indexOfFirstItem + index + 1}
                      </td>

                      {/* Employee Info Column */}
                      <td style={{
                        padding: '16px 24px'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#0a0a0a'
                          }}>
                            {user.full_name}
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span>📧</span>
                            {user.email}
                          </div>
                          {user.employee_code && (
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{
                                padding: '2px 8px',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '4px',
                                fontWeight: '500',
                                fontFamily: 'monospace'
                              }}>
                                {user.employee_code}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Role & Department Column */}
                      <td style={{
                        padding: '16px 24px'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div>
                            {getRoleBadge(user.role)}
                          </div>
                          {user.department && (
                            <div style={{
                              fontSize: '13px',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{ fontWeight: '500' }}>Dept:</span>
                              {user.department}
                            </div>
                          )}
                          {user.designation && (
                            <div style={{
                              fontSize: '13px',
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{ fontWeight: '500' }}>Role:</span>
                              {user.designation}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Reporting Manager Column */}
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {user.manager_id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ fontWeight: '500', color: '#0a0a0a' }}>
                              {getManagerName(user.manager_id)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                              Manager
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '13px' }}>No Manager</span>
                        )}
                      </td>

                      {/* Status Column */}
                      <td style={{
                        padding: '16px 24px'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <Badge bg={user.is_active ? 'success' : 'secondary'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {user.leave_status && (
                            <Badge bg="warning" style={{ textTransform: 'capitalize', fontSize: '11px' }}>
                              On Leave
                            </Badge>
                          )}
                          {user.wfh_status && !user.leave_status && (
                            <Badge bg="info" style={{ fontSize: '11px' }}>
                              WFH
                            </Badge>
                          )}
                        </div>
                      </td>

                      {/* Attendance Column */}
                      {(isAdmin || isHR) && (
                        <td style={{
                          padding: '16px 24px'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{
                              fontSize: '13px',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{ fontWeight: '500', color: '#6b7280' }}>In:</span>
                              <span style={{ fontWeight: '600', color: user.punch_in ? '#059669' : '#9ca3af' }}>
                                {formatTime(user.punch_in)}
                              </span>
                            </div>
                            <div style={{
                              fontSize: '13px',
                              color: '#374151',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{ fontWeight: '500', color: '#6b7280' }}>Out:</span>
                              <span style={{ fontWeight: '600', color: user.punch_out ? '#dc2626' : '#9ca3af' }}>
                                {formatTime(user.punch_out)}
                              </span>
                            </div>
                            {user.total_working_hours !== null && (
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontWeight: '500',
                                marginTop: '2px'
                              }}>
                                {formatWorkingHours(user.total_working_hours)}
                              </div>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Actions Column */}
                      {(isAdmin || isHR) && (
                        <td style={{
                          padding: '16px 24px'
                        }}>
                          <button
                            onClick={() => handleEditClick(user)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: '#635bff',
                              color: 'white',
                              border: 'none',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <FaEdit size={12} />
                            Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
        {filteredUsers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredUsers.length / itemsPerPage)}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredUsers.length}
          />
        )}
      </div>

      {/* Edit Employee Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header style={{
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          backgroundColor: '#faf9ff'
        }}>
          <Modal.Title style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#0a0a0a'
          }}>
            Edit Employee Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          {selectedUser && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Employee: {selectedUser.full_name}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  {selectedUser.email}
                </div>
              </div>

              <Form>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Full Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.full_name}
                    onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                    placeholder="Enter full name"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Mobile Number
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    pattern="[0-9]{10}"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                  <Form.Text className="text-muted">
                    Required for OTP login and password reset
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Employee Code
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.employee_code}
                    onChange={(e) => setEditFormData({ ...editFormData, employee_code: e.target.value })}
                    placeholder="Enter employee code"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Department
                  </Form.Label>
                  <Form.Select
                    value={editFormData.department}
                    onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Designation
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.designation}
                    onChange={(e) => setEditFormData({ ...editFormData, designation: e.target.value })}
                    placeholder="Enter designation"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Reporting Manager
                  </Form.Label>
                  <Form.Select
                    value={editFormData.manager_id}
                    onChange={(e) => setEditFormData({ ...editFormData, manager_id: e.target.value === '' ? null : parseInt(e.target.value) })}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">No Manager</option>
                    {allUsersForManagers
                      .filter(u => u.id !== selectedUser.id)
                      .map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.email})
                        </option>
                      ))
                    }
                  </Form.Select>
                </Form.Group>

                <hr style={{ margin: '24px 0', borderColor: '#e5e7eb' }} />

                <Form.Group className="mb-4">
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <Form.Label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: 0
                    }}>
                      Reset Password
                    </Form.Label>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      style={{
                        fontSize: '13px',
                        textDecoration: 'none',
                        padding: '4px 8px'
                      }}
                    >
                      {showResetPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  {showResetPassword && (
                    <>
                      <Form.Control
                        type="password"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        placeholder="Enter new password (leave blank to keep current)"
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          padding: '12px 14px',
                          fontSize: '14px'
                        }}
                      />
                      <Form.Text className="text-muted">
                        Minimum 6 characters. Leave blank to keep current password.
                      </Form.Text>
                    </>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: editFormData.is_active ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '8px',
                    border: `1px solid ${editFormData.is_active ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    <div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '4px'
                      }}>
                        Account Status
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#6b7280'
                      }}>
                        {editFormData.is_active
                          ? 'User can access the system'
                          : 'User cannot login to the system'}
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      id="is-active-switch"
                      checked={editFormData.is_active}
                      onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                      label={editFormData.is_active ? 'Active' : 'Inactive'}
                      style={{
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    />
                  </div>
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{
          borderTop: '1px solid #e5e7eb',
          padding: '16px 24px',
          backgroundColor: '#fafafa'
        }}>
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(false)}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateUser}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              backgroundColor: '#635bff',
              border: 'none',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Employee Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
        <Modal.Header style={{
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          backgroundColor: '#faf9ff'
        }}>
          <Modal.Title style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#0a0a0a'
          }}>
            Add New Employee
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Full Name <span style={{ color: '#dc2626' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={addFormData.full_name}
                    onChange={(e) => setAddFormData({ ...addFormData, full_name: e.target.value })}
                    placeholder="Enter full name"
                    required
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Email Address <span style={{ color: '#dc2626' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={addFormData.email}
                    onChange={(e) => setAddFormData({ ...addFormData, email: e.target.value })}
                    placeholder="Enter email address"
                    required
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Mobile Number <span style={{ color: '#dc2626' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    value={addFormData.phone}
                    onChange={(e) => setAddFormData({ ...addFormData, phone: e.target.value })}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    pattern="[0-9]{10}"
                    required
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                  <Form.Text className="text-muted">
                    Required for OTP login and password reset
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Employee Code
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={addFormData.employee_code}
                    onChange={(e) => setAddFormData({ ...addFormData, employee_code: e.target.value })}
                    placeholder="Enter employee code"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Department
                  </Form.Label>
                  <Form.Select
                    value={addFormData.department}
                    onChange={(e) => setAddFormData({ ...addFormData, department: e.target.value })}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Designation
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={addFormData.designation}
                    onChange={(e) => setAddFormData({ ...addFormData, designation: e.target.value })}
                    placeholder="Enter designation"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Role
                  </Form.Label>
                  <Form.Select
                    value={addFormData.role}
                    onChange={(e) => setAddFormData({ ...addFormData, role: e.target.value })}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="employee">Employee</option>
                    <option value="hr">HR</option>
                    <option value="admin">Admin</option>
                    <option value="management">Management</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Reporting Manager
                  </Form.Label>
                  <Form.Select
                    value={addFormData.manager_id}
                    onChange={(e) => setAddFormData({ ...addFormData, manager_id: e.target.value })}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      padding: '12px 14px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">No Manager</option>
                    {allUsersForManagers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.full_name} ({user.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Date of Joining
              </Form.Label>
              <Form.Control
                type="date"
                value={addFormData.date_of_joining}
                onChange={(e) => setAddFormData({ ...addFormData, date_of_joining: e.target.value })}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  padding: '12px 14px',
                  fontSize: '14px'
                }}
              />
            </Form.Group>

            <div style={{
              padding: '16px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px',
              border: '1px solid #bfdbfe',
              marginTop: '16px'
            }}>
              <div style={{
                fontSize: '13px',
                color: '#1e40af',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                📧 Welcome Email
              </div>
              <div style={{
                fontSize: '13px',
                color: '#3b82f6',
                lineHeight: '1.5'
              }}>
                Upon adding, the employee will receive an email with:
                <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                  <li>Instructions to set their password</li>
                  <li>How to login using email/password or mobile OTP</li>
                  <li>Portal access information</li>
                </ul>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{
          borderTop: '1px solid #e5e7eb',
          padding: '16px 24px',
          backgroundColor: '#fafafa'
        }}>
          <Button
            variant="secondary"
            onClick={() => setShowAddModal(false)}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddEmployee}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              backgroundColor: '#635bff',
              border: 'none',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Add Employee
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Users;
